import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { WinnerWithMetadata, WinnerSource } from '../types/types';
import teamsData from '../data/teams.json';
import { Team } from '../types/types';
import ResponsiveBracketVisualizer from './ResponsiveBracketVisualizer';
import SimulationSection from './SimulationSection';
import RootingInterests from './RootingInterests';
import GeneratedStandings from './GeneratedStandings';
import PickBreakdownPanel from './PickBreakdownPanel';
import ErrorDisplay from './ErrorDisplay';
import FloatingStandings from './FloatingStandings';
import UnifiedPoolScenarioBar from './UnifiedPoolScenarioBar';
import OnboardingWizard from './OnboardingWizard';
import AppHeader from './layout/AppHeader';
import Sidebar from './layout/Sidebar';
import MobileNav, { MobileTab } from './layout/MobileNav';
import { ChevronUp, ChevronDown } from 'lucide-react';
import { isBracketComplete, getTeamSeed } from '../utils/bracketUtils';
import { computePickBreakdowns, GameBreakdown } from '../utils/pickBreakdownUtils';
import { updatePoolPinnedPlayers } from '../api/api';
import { useDynamicPageSize } from '../hooks/useDynamicPageSize';
import { cn } from '../lib/utils';
import { AdUnit, AdUnitRef, useAdRefresh } from '../ads';

const teams = teamsData as Team[];

interface ResponsiveLayoutProps {
  winnersWithMetadata: WinnerWithMetadata[];
  validationError: string | null;
  playerPicks: string;
  playerNames: string[];
  selectedPlayer: string | null;
  errorMessage: string | null;
  hasUserMadeChanges: boolean;
  forceArrayUpdate: number;
  onWinnersUpdate: (winners: WinnerWithMetadata[], error?: string) => void;
  onValidationError: (error: string | null) => void;
  onUserBracketChanges: (updatedWinners: WinnerWithMetadata[]) => void;
  onLoadPlayerScenario: (winners: WinnerWithMetadata[], playerName: string, validationError?: string) => void;
  onPlayerPicksChange: (picks: string) => void;
  onPlayersExtracted: (players: string[]) => void;
  onPlayerSelect: (playerName: string) => void;
  onCloseRootingInterests: () => void;
  onLoadBracket: (loadedWinners: string[]) => void;
  onErrorDismiss: () => void;
  onFillWithHigherSeeds: () => void;
  onFillWithLowerSeeds: () => void;
  onClearBracket: () => void;
}

const ResponsiveLayout: React.FC<ResponsiveLayoutProps> = ({
  winnersWithMetadata,
  validationError,
  playerPicks,
  playerNames,
  selectedPlayer,
  errorMessage,
  hasUserMadeChanges,
  forceArrayUpdate,
  onWinnersUpdate,
  onValidationError,
  onUserBracketChanges,
  onLoadPlayerScenario,
  onPlayerPicksChange,
  onPlayersExtracted,
  onPlayerSelect,
  onCloseRootingInterests,
  onLoadBracket,
  onErrorDismiss,
  onFillWithHigherSeeds,
  onFillWithLowerSeeds,
  onClearBracket
}) => {
  const generatedStandingsRef = useRef<HTMLDivElement>(null);
  const [showFloatingStandings, setShowFloatingStandings] = useState(false);
  const [pinnedPlayers, setPinnedPlayers] = useState<string[]>([]);
  const [currentPoolName, setCurrentPoolName] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileTab, setMobileTab] = useState<MobileTab>('bracket');
  const [isMobile, setIsMobile] = useState(false);
  const [mobileFloatingMinimized, setMobileFloatingMinimized] = useState(false);
  const [mobileBracketTab, setMobileBracketTab] = useState<'scenario' | 'breakdown'>('scenario');
  const dynamicPageSize = useDynamicPageSize();
  const mobileAdRef = useRef<AdUnitRef>(null);
  useAdRefresh(mobileAdRef, mobileTab);

  // Desktop bracket tab state (controlled from here so sidebar can react)
  const [bracketTab, setBracketTab] = useState<'scenario' | 'breakdown'>('scenario');
  const [selectedBreakdownGame, setSelectedBreakdownGame] = useState<number | null>(null);

  // Clear selected game when switching tabs
  useEffect(() => {
    setSelectedBreakdownGame(null);
  }, [bracketTab]);

  // Compute pick breakdowns for desktop (shared between bracket + sidebar)
  const breakdownMap = useMemo(() => {
    if (!playerPicks) return new Map<number, GameBreakdown>();
    const officialWinners = winnersWithMetadata.map(w =>
      w && w.source === WinnerSource.OFFICIAL ? w.team : ''
    );
    const breakdowns = computePickBreakdowns(playerPicks, officialWinners);
    const map = new Map<number, GameBreakdown>();
    for (const b of breakdowns) map.set(b.gameId, b);
    return map;
  }, [playerPicks, winnersWithMetadata]);

  const selectedBreakdown = selectedBreakdownGame !== null ? breakdownMap.get(selectedBreakdownGame) ?? null : null;

  // Portal targets for pool selector (above standings)
  const [desktopPoolSelectorNode, setDesktopPoolSelectorNode] = useState<HTMLDivElement | null>(null);
  const [mobilePoolSelectorNode, setMobilePoolSelectorNode] = useState<HTMLDivElement | null>(null);

  // Portal targets for scenario selector dropdown (in bracket heading)
  const [scenarioSelectorNode, setScenarioSelectorNode] = useState<HTMLDivElement | null>(null);
  const [mobileScenarioSelectorNode, setMobileScenarioSelectorNode] = useState<HTMLDivElement | null>(null);

  // Save scenario trigger counter (incremented to open dialog in UnifiedPoolScenarioBar)
  const [saveDialogTrigger, setSaveDialogTrigger] = useState(0);

  // Share scenario trigger counter (incremented to open share dialog in UnifiedPoolScenarioBar)
  const [shareDialogTrigger, setShareDialogTrigger] = useState(0);

  // Add pool trigger counter (incremented to open AddPoolModal from onboarding wizard)
  const [advancedPoolTrigger, setAddPoolTrigger] = useState(0);

  // Track mobile vs desktop to compute floating standings visibility
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 1023px)');
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const handlePoolChange = (poolName: string | null, poolPinnedPlayers: string[]) => {
    setCurrentPoolName(poolName);
    setPinnedPlayers(poolPinnedPlayers);
  };

  const pinSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    return () => { if (pinSaveTimerRef.current) clearTimeout(pinSaveTimerRef.current); };
  }, []);

  const handlePinnedPlayersChange = useCallback((newPinned: string[]) => {
    setPinnedPlayers(newPinned);
    if (!currentPoolName) return;
    if (pinSaveTimerRef.current) clearTimeout(pinSaveTimerRef.current);
    pinSaveTimerRef.current = setTimeout(() => {
      updatePoolPinnedPlayers(currentPoolName, newPinned).catch(err => {
        console.error('Failed to save pinned players:', err);
      });
    }, 300);
  }, [currentPoolName]);

  const [showOnboarding, setShowOnboarding] = useState(false);
  const [hasPool, setHasPool] = useState(false);
  const [wizardImportedPool, setWizardImportedPool] = useState<string | null>(null);

  const handleHelpClick = () => setShowOnboarding(true);
  const handleCloseOnboarding = () => setShowOnboarding(false);
  const handleOpenAdvancedFromWizard = () => {
    setShowOnboarding(false);
    setAddPoolTrigger(prev => prev + 1);
  };

  const handlePoolsLoaded = (poolNames: string[]) => {
    const realPools = poolNames.filter(name => name !== 'Sample Pool');
    setHasPool(realPools.length > 0);
    const hasCompleted = localStorage.getItem('hasCompletedOnboarding');
    if (!hasCompleted && realPools.length === 0) {
      setShowOnboarding(true);
    }
  };

  const handleWizardImportSuccess = (poolName: string) => {
    setHasPool(true);
    setWizardImportedPool(poolName);
  };

  const handleFirstPoolImport = () => {
    const hasCompleted = localStorage.getItem('hasCompletedOnboarding');
    if (!hasCompleted && !hasPool) {
      setShowOnboarding(true);
    }
    setHasPool(true);
  };

  const knownWinnersArray = winnersWithMetadata.map(winner => winner?.team || '');

  useEffect(() => {
    // Reset on tab switch — observer will recalculate once the node is visible
    setShowFloatingStandings(false);

    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowFloatingStandings(!entry.isIntersecting);
      },
      { threshold: 0, rootMargin: '-50px 0px' }
    );

    const node = generatedStandingsRef.current;
    if (node) {
      observer.observe(node);
    }

    return () => {
      if (node) {
        observer.unobserve(node);
      }
    };
  }, [bracketTab]);

  // Desktop standings get the IntersectionObserver ref for floating standings
  const desktopStandingsContent = (
    <GeneratedStandings
      playerPicks={playerPicks}
      winnersWithMetadata={winnersWithMetadata}
      bracketComplete={isBracketComplete(knownWinnersArray)}
      onLoadPlayerScenario={onLoadPlayerScenario}
      headerRef={generatedStandingsRef}
      pinnedPlayers={pinnedPlayers}
      onPinnedPlayersChange={handlePinnedPlayersChange}
      pageSize={dynamicPageSize}
    />
  );

  // Mobile standings without the ref (prevents IntersectionObserver from tracking hidden element)
  const mobileStandingsContent = (
    <GeneratedStandings
      playerPicks={playerPicks}
      winnersWithMetadata={winnersWithMetadata}
      bracketComplete={isBracketComplete(knownWinnersArray)}
      onLoadPlayerScenario={onLoadPlayerScenario}
      pinnedPlayers={pinnedPlayers}
      onPinnedPlayersChange={handlePinnedPlayersChange}
      pageSize={dynamicPageSize}
    />
  );

  const simulationContent = (
    <SimulationSection
      playerPicks={playerPicks}
      knownWinners={knownWinnersArray}
      onPlayerSelect={onPlayerSelect}
      pinnedPlayers={pinnedPlayers}
      onPinnedPlayersChange={handlePinnedPlayersChange}
      pageSize={dynamicPageSize}
      activePoolName={currentPoolName}
    />
  );

  // Main bracket content
  const bracketContent = (
    <>
      <ErrorDisplay error={errorMessage} onDismiss={onErrorDismiss} />

      <div className="mb-4">
        <UnifiedPoolScenarioBar
          currentPlayerPicks={playerPicks}
          onPlayerPicksChange={onPlayerPicksChange}
          onPlayersExtracted={onPlayersExtracted}
          winnersWithMetadata={winnersWithMetadata}
          onWinnersUpdate={onWinnersUpdate}
          onValidationError={onValidationError}
          hasUserMadeChanges={hasUserMadeChanges}
          onFillWithHigherSeeds={onFillWithHigherSeeds}
          onFillWithLowerSeeds={onFillWithLowerSeeds}
          onClearBracket={onClearBracket}
          onFirstPoolImport={handleFirstPoolImport}
          onPoolChange={handlePoolChange}
          onPoolsLoaded={handlePoolsLoaded}
          externalImportPoolName={wizardImportedPool}
          poolSelectorPortal={desktopPoolSelectorNode}
          scenarioSelectorPortal={scenarioSelectorNode}
          saveDialogTrigger={isMobile ? undefined : saveDialogTrigger}
          shareDialogTrigger={isMobile ? undefined : shareDialogTrigger}
          advancedPoolTrigger={isMobile ? undefined : advancedPoolTrigger}
        />
      </div>

      <div>
        <ResponsiveBracketVisualizer
          winnersWithMetadata={winnersWithMetadata}
          onUserBracketChanges={onUserBracketChanges}
          onClearBracket={onClearBracket}
          onSaveScenario={() => setSaveDialogTrigger(n => n + 1)}
          onShareScenario={() => setShareDialogTrigger(n => n + 1)}
          hasUnsavedChanges={hasUserMadeChanges}
          scenarioSelectorRef={setScenarioSelectorNode}
          playerPicks={playerPicks}
          pinnedPlayers={pinnedPlayers}
          onPinnedPlayersChange={handlePinnedPlayersChange}
          activeTab={bracketTab}
          onTabChange={setBracketTab}
          selectedBreakdownGame={selectedBreakdownGame}
          onBreakdownGameSelect={setSelectedBreakdownGame}
          breakdownMap={breakdownMap}
        />
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex flex-col">
      <AppHeader
        onHelpClick={handleHelpClick}
        sidebarCollapsed={sidebarCollapsed}
        onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
        knownWinners={knownWinnersArray}
      />

      <div className="flex flex-1 overflow-hidden">
        {/* Desktop sidebar */}
        <Sidebar collapsed={sidebarCollapsed}>
          <div ref={setDesktopPoolSelectorNode} className="mb-3" />
          {bracketTab === 'breakdown' ? (
            // Breakdown mode: show game detail panel or placeholder
            selectedBreakdown ? (
              <PickBreakdownPanel
                key={selectedBreakdownGame}
                breakdown={selectedBreakdown}
                pinnedPlayers={pinnedPlayers}
                onPinnedPlayersChange={handlePinnedPlayersChange}
                getTeamSeed={(teamName) => getTeamSeed(teams, teamName)}
                onClose={() => setSelectedBreakdownGame(null)}
              />
            ) : (
              <div className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground">
                <p className="text-sm">Click a game in the bracket to see the pick breakdown.</p>
              </div>
            )
          ) : (
            // Scenario mode: simulation + standings
            <>
              {simulationContent}
              <div className="my-4 flex justify-center">
                <AdUnit slotId="sidebar-rectangle" adSlot="8000691473" width={300} height={250} />
              </div>
              {desktopStandingsContent}
            </>
          )}
        </Sidebar>

        {/* Main content area */}
        <main className="flex-1 overflow-y-auto p-4 pb-[calc(5rem+max(13px,env(safe-area-inset-bottom,0px)))] lg:pb-4">
          {/* Mobile banner ad */}
          <div className="lg:hidden mb-4">
            <AdUnit ref={mobileAdRef} slotId="mobile-banner" adSlot="6687609808" adFormat="auto" fullWidthResponsive={true} />
          </div>

          {/* Desktop: always show bracket */}
          <div className="hidden lg:block">
            {bracketContent}
          </div>

          {/* Mobile: tab-based views (use CSS hidden/block to keep components mounted) */}
          <div className="lg:hidden">
            <div className={mobileTab === 'bracket' ? '' : 'hidden'}>
              <ErrorDisplay error={errorMessage} onDismiss={onErrorDismiss} />
              {mobileBracketTab === 'scenario' && (
                <div className="mb-4">
                  <UnifiedPoolScenarioBar
                    currentPlayerPicks={playerPicks}
                    onPlayerPicksChange={onPlayerPicksChange}
                    onPlayersExtracted={onPlayersExtracted}
                    winnersWithMetadata={winnersWithMetadata}
                    onWinnersUpdate={onWinnersUpdate}
                    onValidationError={onValidationError}
                    hasUserMadeChanges={hasUserMadeChanges}
                    onFillWithHigherSeeds={onFillWithHigherSeeds}
                    onFillWithLowerSeeds={onFillWithLowerSeeds}
                    onClearBracket={onClearBracket}
                    onFirstPoolImport={handleFirstPoolImport}
                    onPoolChange={handlePoolChange}
                    onPoolsLoaded={handlePoolsLoaded}
                    externalImportPoolName={wizardImportedPool}
                    poolSelectorPortal={mobilePoolSelectorNode}
                    scenarioSelectorPortal={mobileScenarioSelectorNode}
                    saveDialogTrigger={isMobile ? saveDialogTrigger : undefined}
                    shareDialogTrigger={isMobile ? shareDialogTrigger : undefined}
                    advancedPoolTrigger={isMobile ? advancedPoolTrigger : undefined}
                  />
                </div>
              )}
              <div>
                <ResponsiveBracketVisualizer
                  winnersWithMetadata={winnersWithMetadata}
                  onUserBracketChanges={onUserBracketChanges}
                  onClearBracket={onClearBracket}
                  onSaveScenario={() => setSaveDialogTrigger(n => n + 1)}
                  onShareScenario={() => setShareDialogTrigger(n => n + 1)}
                  hasUnsavedChanges={hasUserMadeChanges}
                  scenarioSelectorRef={setMobileScenarioSelectorNode}
                  playerPicks={playerPicks}
                  pinnedPlayers={pinnedPlayers}
                  onPinnedPlayersChange={handlePinnedPlayersChange}
                  onTabChange={setMobileBracketTab}
                  breakdownMap={breakdownMap}
                />
              </div>
              {/* Spacer so championship can scroll above bottom FloatingStandings */}
              <div className="h-[250px]" />
            </div>
            <div className={mobileTab === 'standings' ? 'space-y-4' : 'hidden'}>
              <div ref={setMobilePoolSelectorNode} />
              {mobileStandingsContent}
            </div>
            <div className={mobileTab === 'simulate' ? 'space-y-4' : 'hidden'}>
              {simulationContent}
            </div>
          </div>
        </main>
      </div>

      {/* Floating Standings — desktop: top-left overlay; mobile: minimizable top strip */}
      {(() => {
        const mobileVisible = isMobile && mobileTab === 'bracket';
        const desktopVisible = false;

        return (
          <div
            className={cn(
              "fixed z-40 border bg-card/95 backdrop-blur transition-all duration-300",
              // Desktop positioning
              "lg:left-5 lg:right-auto lg:bottom-auto lg:top-[10%] lg:w-[340px] lg:max-h-[400px] lg:overflow-y-auto lg:overflow-x-hidden lg:p-3 lg:rounded-xl lg:shadow-xl lg:border-border/60",
              // Mobile positioning — bottom of screen, above MobileNav (accounts for safe area / system nav)
              "left-0 right-0 bottom-[calc(3.5rem+max(13px,env(safe-area-inset-bottom,0px)))] rounded-t-xl shadow-lg",
              // Visibility
              mobileVisible
                ? "opacity-100 translate-y-0"
                : desktopVisible
                  ? "opacity-100 lg:translate-x-0"
                  : "opacity-0 pointer-events-none translate-y-full lg:-translate-x-full lg:translate-y-0"
            )}
          >
            {/* Mobile: minimized bar */}
            {isMobile && mobileFloatingMinimized && (
              <button
                className="w-full flex items-center justify-between px-4 py-2 text-sm font-medium text-foreground"
                onClick={() => setMobileFloatingMinimized(false)}
              >
                <span>Pinned Players</span>
                <ChevronUp className="h-4 w-4 text-muted-foreground" />
              </button>
            )}

            {/* Mobile: expanded content with minimize button */}
            {isMobile && !mobileFloatingMinimized && (
              <div className="overflow-x-hidden p-3">
                <button
                  className="absolute top-1 right-2 p-1 text-muted-foreground hover:text-foreground"
                  onClick={() => setMobileFloatingMinimized(true)}
                  title="Minimize"
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
                <FloatingStandings
                  playerPicks={playerPicks}
                  winnersWithMetadata={winnersWithMetadata}
                  maxPlayers={4}
                  pinnedPlayers={pinnedPlayers}
                  onPinnedPlayersChange={handlePinnedPlayersChange}
                />
              </div>
            )}

            {/* Desktop: always show full content */}
            {!isMobile && (
              <FloatingStandings
                playerPicks={playerPicks}
                winnersWithMetadata={winnersWithMetadata}
                maxPlayers={4}
                pinnedPlayers={pinnedPlayers}
                onPinnedPlayersChange={handlePinnedPlayersChange}
              />
            )}
          </div>
        );
      })()}

      {/* Mobile bottom navigation */}
      <MobileNav activeTab={mobileTab} onTabChange={setMobileTab} />

      {/* Rooting Interests Modal */}
      {selectedPlayer && (
        <RootingInterests
          playerName={selectedPlayer}
          playerPicks={playerPicks}
          knownWinners={knownWinnersArray}
          onClose={onCloseRootingInterests}
          activePoolName={currentPoolName}
        />
      )}

      {/* Onboarding Wizard */}
      <OnboardingWizard
        isOpen={showOnboarding}
        onClose={handleCloseOnboarding}
        onImportSuccess={handleWizardImportSuccess}
        hasPool={hasPool}
        onOpenAdvanced={handleOpenAdvancedFromWizard}
      />
    </div>
  );
};

export default ResponsiveLayout;
